import React, { useState } from 'react';
import {
  loadUsersModifiedByMonthByUser,
  exportUsersModifiedByMonthByUser,
} from '../../../../actions/reportByMonthActions';
import { Table, SimpleTextFilter, TableColumn, getVisibleColumns } from '../../../common/Table';
import { CommaNumber_NoDecimal, capitalizeFirstLetter } from '../../../../util/formatter';
import Loading from '../../../common/Loading';
import KwPanel from '../../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../../hooks/useTableSettings';
import { useReportYear } from '../../../../hooks/useReportYear';
import YearFormSelect from '../YearFormSelect';
import UserLink from '../../../common/links/UserLink';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export interface UsersModifiedByMonthByUserListProps { tableViewSwitcher?: React.ReactNode }
function UsersModifiedByMonthByUserList({ tableViewSwitcher }: UsersModifiedByMonthByUserListProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { year, years, makeHandleYearChange } = useReportYear();
  const [reload, setReload] = useState<Date | undefined>();
  const months = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
  ];
  const allColumns = ['fullName', 'year'];
  for (const month of months) {
    allColumns.push(month);
  }
  const defaultVisibleColumns = allColumns;
  const [items, setItems] = useState<any>();
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
    parameters,
  } = useTableSettings(
    'usersmodifiedbymonthbyuserlist-settings',
    getTableSettingsDefaults({
      defaultSort: 'fullName',
      defaultVisibleColumns,
    }),
    { year },
    loadUsersModifiedByMonthByUser,
    setItems,
    reload,
    t('Failed to load report')
  );

  // The base config of the columns, this will be filtered based on the visible (above)
  let columnsConfig: TableColumn[] = [
    {
      Key: 'fullName',
      Label: t('Uploading user'),
      Render: (item) => (
        <UserLink id={item.userProfileId} name={item.fullName} imageSize={32} />
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'year',
      Label: t('Year'),
      Render: (item) => item.year,
    },
  ];
  // Add the months to the array
  for (const month of months) {
    columnsConfig.push({
      Key: month,
      Label: t(capitalizeFirstLetter(month)),
      className: 'text-right num no-wrap',
      Render: (row) => CommaNumber_NoDecimal(row[month]),
    });
  }

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);
  const handleYearChange = makeHandleYearChange(parameters, setReload);

  // return an alaska table
  return items ? (
    <>
      <div className="form-horizontal row px-4 px-md-0">
        <div className="col-md-6">
          <YearFormSelect
            year={year}
            years={years}
            handleYearChange={handleYearChange}
          ></YearFormSelect>
        </div>
      </div>
      <KwPanel className="mb-4">
        <Table
          className="table-v-stripes"
          columns={columns}
          allColumns={allColumns}
          rows={items.data.map((x: any) => {
            return { Key: x.uploadinguserId, ...x };
          })}
          totalRowCount={items.totalRowCount}
          filteredRowCount={items.filteredRowCount}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          isLoading={isLoading}
          filters={filters}
          filterChange={filterChange}
          exportData={(filters, scopes) =>
            exportUsersModifiedByMonthByUser(year, filters, scopes)
          }
          tableActionBarButtons={tableViewSwitcher}
        />
      </KwPanel>
    </>
  ) : (
    <Loading></Loading>
  );
}
export default UsersModifiedByMonthByUserList;
